import React, { useState } from 'react'
import axios from 'axios'
import { Tab, Tabs } from 'react-bootstrap'
// @ts-ignore - react-select ships its own types, but @types/react-select shadows them here.
import Select from 'react-select'
import Modal from '../../shared/modal'
import ChipListInput from '../../shared/ChipListInput'
import RemovableChip from '../../shared/RemovableChip'
import ScenarioTestPanel from './ScenarioTestPanel'
import { defaultEmailMessage, defaultSmsMessage } from './messageDefaults'
import { BusinessErrorOption, ScenarioDraft, ScenarioRow } from './types'
import {
  admin_alert_automation_scenario_path,
  admin_alert_automation_scenarios_path
} from '../../../../routes'

interface ScenarioFormModalProps {
  businessErrors: BusinessErrorOption[]
  periodUnits: string[]
  smsMaxLength: number
  scenario?: ScenarioRow
  canTest: boolean
  onClose: () => void
  onSaved: () => void
}

// Same regex the server validates with (Customer::PHONE_NUMBER_REGEX). The server additionally
// insists on a minimum length, so a number accepted here can still be refused on save.
const PHONE_PATTERN = /^\+?[1-9]\d{1,14}$/
const EMAIL_PATTERN = /^[^@\s]+@[^@\s]+\.[^@\s]+$/

const DEFAULT_THRESHOLD_COUNT = 3
const DEFAULT_PERIOD_VALUE = 15

const PERIOD_UNIT_SECONDS: Record<string, number> = { minutes: 60, hours: 3600, days: 86400 }

// A rate-limited error stores no row for the occurrences it suppresses, so the most a window can
// ever hold is one at the start plus one per interval after it. A threshold above that is a
// scenario that can never fire, and nothing else in the form would say so.
const unreachableCodes = (draft: ScenarioDraft, businessErrors: BusinessErrorOption[]): string[] => {
  const periodValue = Number(draft.threshold_period_value)
  const count = Number(draft.threshold_count)
  // A period of 0 has no window, so any threshold is reached eventually.
  if (!periodValue || !count) return []

  const windowSeconds = periodValue * (PERIOD_UNIT_SECONDS[draft.threshold_period_unit] || 0)

  return draft.business_error_codes.filter((code) => {
    const interval = businessErrors.find((entry) => entry.code === code)?.min_interval_seconds
    if (!interval) return false

    return count > Math.floor(windowSeconds / interval) + 1
  })
}

const draftFrom = (scenario: ScenarioRow | undefined, periodUnits: string[]): ScenarioDraft => ({
  name: scenario?.name || '',
  business_error_codes: scenario?.business_error_codes || [],
  threshold_count: String(scenario?.threshold_count ?? DEFAULT_THRESHOLD_COUNT),
  threshold_period_value: String(scenario?.threshold_period_value ?? DEFAULT_PERIOD_VALUE),
  threshold_period_unit: scenario?.threshold_period_unit || periodUnits[0],
  sms_enabled: scenario?.sms_enabled ?? true,
  phone_numbers: scenario?.phone_numbers || [],
  sms_message: scenario?.sms_message || '',
  email_enabled: scenario?.email_enabled ?? true,
  email_addresses: scenario?.email_addresses || [],
  email_message: scenario?.email_message || '',
  active: scenario?.active ?? true
})

const ScenarioFormModal = ({
  businessErrors,
  periodUnits,
  smsMaxLength,
  scenario,
  canTest,
  onClose,
  onSaved
}: ScenarioFormModalProps) => {
  const isEdit = scenario !== undefined

  const [draft, setDraft] = useState<ScenarioDraft>(draftFrom(scenario, periodUnits))
  const [testOpen, setTestOpen] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const update = <K extends keyof ScenarioDraft>(key: K, value: ScenarioDraft[K]) =>
    setDraft((current) => ({ ...current, [key]: value }))

  // The bodies follow the error selection only while they still read as the suggestion for the
  // codes selected a moment ago. Once an operator types their own wording, changing the selection
  // must not overwrite it - so the previous suggestion is derived from the codes rather than
  // remembered, which also keeps this updater pure.
  const changeCodes = (codes: string[]) =>
    setDraft((current) => {
      const smsFor = (values: string[]) => defaultSmsMessage(values).slice(0, smsMaxLength)
      const emailFor = (values: string[]) => defaultEmailMessage(values, businessErrors)
      const smsIsOurs = current.sms_message === smsFor(current.business_error_codes)
      const emailIsOurs = current.email_message === emailFor(current.business_error_codes)

      return {
        ...current,
        business_error_codes: codes,
        sms_message: smsIsOurs ? smsFor(codes) : current.sms_message,
        email_message: emailIsOurs ? emailFor(codes) : current.email_message
      }
    })

  const submit = () => {
    setSubmitting(true)
    setError(null)

    const request = isEdit
      ? axios.put(admin_alert_automation_scenario_path(scenario.id), draft)
      : axios.post(admin_alert_automation_scenarios_path(), draft)

    request
      .then(() => onSaved())
      .catch((err) => {
        setError(err.response?.data?.error || `Could not ${isEdit ? 'update' : 'create'} the scenario.`)
        setSubmitting(false)
      })
  }

  const options = businessErrors.map((entry) => ({
    value: entry.code,
    label: `${entry.description} (${entry.code})`
  }))

  const periodUnitLabel = draft.threshold_period_value === '1'
    ? draft.threshold_period_unit.replace(/s$/, '')
    : draft.threshold_period_unit

  const unreachable = unreachableCodes(draft, businessErrors)

  // Mirrors AlertScenario#threshold_label: a period of 0 is no window at all, so the count simply
  // accumulates until it is met. An emptied field is not that - Number('') is 0, and summarising it
  // as "no time limit" would promise a configuration the save then rejects as not a number.
  const thresholdSummary = draft.threshold_period_value !== '' && Number(draft.threshold_period_value) === 0
    ? `${draft.threshold_count}× (no time limit)`
    : `${draft.threshold_count}× in ${draft.threshold_period_value} ${periodUnitLabel}`

  const recipientsSummary = () => {
    const parts: string[] = []
    if (draft.sms_enabled) parts.push(`${draft.phone_numbers.length} SMS`)
    if (draft.email_enabled) parts.push(`${draft.email_addresses.length} Email`)

    return parts.length > 0 ? parts.join(' · ') : 'None added'
  }

  const summaryBox = (
    <div className="bg-light rounded p-3 mt-3 small">
      <div>
        <span className="fw-bold">Errors:</span>{' '}
        {draft.business_error_codes.length > 0 ? draft.business_error_codes.join(', ') : 'None selected'}
      </div>
      <div>
        <span className="fw-bold">Threshold:</span>{' '}
        {thresholdSummary}
      </div>
      <div><span className="fw-bold">Recipients:</span> {recipientsSummary()}</div>
    </div>
  )

  const footer = (
    <div className="d-flex justify-content-between align-items-center w-100">
      <div className="form-check form-switch mb-0">
        <input
          className="form-check-input"
          type="checkbox"
          role="switch"
          id="scenario-active"
          checked={draft.active}
          onChange={(event) => update('active', event.target.checked)}
        />
        <label className="form-check-label" htmlFor="scenario-active">Active</label>
      </div>

      <div className="d-flex gap-2">
        {canTest && (
          <button type="button" className="btn btn-outline-secondary" onClick={() => setTestOpen(true)}>
            Test
          </button>
        )}
        <button type="button" className="btn btn-outline-secondary" onClick={onClose}>Cancel</button>
        <button type="button" className="btn btn-dark" disabled={submitting} onClick={submit}>
          {submitting ? 'Saving…' : 'Save Scenario'}
        </button>
      </div>
    </div>
  )

  return (
    <Modal
      headerText={isEdit ? 'Edit Scenario' : 'Create Scenario'}
      onClose={onClose}
      footerContent={footer}
      widen
    >
      <Tabs defaultActiveKey="summary" transition={false} className="mb-3">
        <Tab eventKey="summary" title="Summary">
          <div className="mb-3">
            <label className="form-label" htmlFor="scenario-name">Name</label>
            <input
              id="scenario-name"
              type="text"
              className="form-control"
              placeholder="e.g. Robot stopped alert"
              value={draft.name}
              onChange={(event) => update('name', event.target.value)}
            />
          </div>

          <div className="mb-3">
            <label className="form-label" htmlFor="scenario-errors">
              Business errors <span className="text-muted">(any of these triggers the scenario)</span>
            </label>
            <Select
              classNamePrefix="react-select"
              className="react-select"
              inputId="scenario-errors"
              options={options}
              value={null}
              controlShouldRenderValue={false}
              placeholder="Search errors by name or code..."
              onChange={(option: { value: string } | null) => {
                if (option && !draft.business_error_codes.includes(option.value)) {
                  changeCodes([...draft.business_error_codes, option.value])
                }
              }}
            />
            <div className="d-flex flex-wrap gap-2 mt-2">
              {draft.business_error_codes.map((code) => {
                const entry = businessErrors.find((option) => option.code === code)

                return (
                  <RemovableChip
                    key={code}
                    label={entry ? `${entry.description} (${code})` : code}
                    onRemove={() => changeCodes(draft.business_error_codes.filter((value) => value !== code))}
                  />
                )
              })}
            </div>
          </div>

          <div className="mb-3">
            <label className="form-label" htmlFor="scenario-threshold-count">Threshold</label>
            <div className="d-flex align-items-center gap-2">
              <input
                id="scenario-threshold-count"
                type="number"
                min={1}
                className="form-control"
                style={{ maxWidth: '90px' }}
                value={draft.threshold_count}
                onChange={(event) => update('threshold_count', event.target.value)}
              />
              <span>times within</span>
              <label className="visually-hidden" htmlFor="scenario-period-value">Period</label>
              <input
                id="scenario-period-value"
                type="number"
                min={0}
                className="form-control"
                style={{ maxWidth: '90px' }}
                value={draft.threshold_period_value}
                onChange={(event) => update('threshold_period_value', event.target.value)}
              />
              <label className="visually-hidden" htmlFor="scenario-period-unit">Period unit</label>
              <select
                id="scenario-period-unit"
                className="form-select"
                style={{ maxWidth: '140px' }}
                value={draft.threshold_period_unit}
                onChange={(event) => update('threshold_period_unit', event.target.value)}
              >
                {periodUnits.map((unit) => <option key={unit} value={unit}>{unit}</option>)}
              </select>
            </div>

            {unreachable.length > 0 && (
              <div className="alert alert-warning mt-2 mb-0 small">
                {`Reported too rarely to reach this threshold: ${unreachable.join(', ')}. `}
                {'This scenario would never fire — lower the count or lengthen the period.'}
              </div>
            )}
          </div>
        </Tab>

        <Tab eventKey="recipients" title="Recipients">
          <div className="form-check form-switch mb-2">
            <input
              className="form-check-input"
              type="checkbox"
              role="switch"
              id="scenario-sms-enabled"
              checked={draft.sms_enabled}
              onChange={(event) => update('sms_enabled', event.target.checked)}
            />
            <label className="form-check-label fw-bold" htmlFor="scenario-sms-enabled">SMS</label>
          </div>

          <div className="mb-3">
            <label className="form-label" htmlFor="scenario-phone-numbers">Phone numbers</label>
            <ChipListInput
              inputId="scenario-phone-numbers"
              values={draft.phone_numbers}
              disabled={!draft.sms_enabled}
              placeholder="+1 415 555 0100 — press Enter to add"
              validate={(value) =>
                PHONE_PATTERN.test(value) ? null : 'Enter a valid phone number (e.g. +37120000000)'}
              onChange={(values) => update('phone_numbers', values)}
            />

            <label className="form-label mt-3" htmlFor="scenario-sms-message">SMS message</label>
            <textarea
              id="scenario-sms-message"
              className="form-control message-field"
              rows={3}
              maxLength={smsMaxLength}
              disabled={!draft.sms_enabled}
              placeholder="Message sent to each phone number..."
              value={draft.sms_message}
              onChange={(event) => update('sms_message', event.target.value)}
            />
            <div className="text-muted small text-end">{draft.sms_message.length}/{smsMaxLength}</div>
          </div>

          <div className="form-check form-switch mb-2">
            <input
              className="form-check-input"
              type="checkbox"
              role="switch"
              id="scenario-email-enabled"
              checked={draft.email_enabled}
              onChange={(event) => update('email_enabled', event.target.checked)}
            />
            <label className="form-check-label fw-bold" htmlFor="scenario-email-enabled">Email</label>
          </div>

          <div className="mb-3">
            <label className="form-label" htmlFor="scenario-email-addresses">Email addresses</label>
            <ChipListInput
              inputId="scenario-email-addresses"
              values={draft.email_addresses}
              disabled={!draft.email_enabled}
              placeholder="ops@roboeatz.com — press Enter to add"
              validate={(value) => (EMAIL_PATTERN.test(value) ? null : 'Enter a valid email address')}
              onChange={(values) => update('email_addresses', values)}
            />

            <label className="form-label mt-3" htmlFor="scenario-email-message">Email message</label>
            <textarea
              id="scenario-email-message"
              className="form-control message-field"
              rows={5}
              disabled={!draft.email_enabled}
              placeholder="Message sent to each email address..."
              value={draft.email_message}
              onChange={(event) => update('email_message', event.target.value)}
            />
          </div>
        </Tab>
      </Tabs>

      {summaryBox}

      {error && <div className="text-danger small mt-2">{error}</div>}

      {testOpen && <ScenarioTestPanel draft={draft} onClose={() => setTestOpen(false)} />}
    </Modal>
  )
}

export default ScenarioFormModal
